import { CSSProperties, RefObject, useLayoutEffect, useState } from "react"

type ViewportRect = { top: number; left: number; width: number; height: number }
const finite = (value: number) => Number.isFinite(value) && Math.abs(value) <= 100000
const dimension = (value: number) => finite(value) && value > 0

/** Fixed-position coordinates are relative to the layout viewport, not the document.
 * VisualViewport offsets and sizes are already CSS pixels: do not add page scroll,
 * multiply by scale, or infer keyboard height from a device/status-bar constant.
 */
function readViewport(previous: ViewportRect | null): ViewportRect | null {
  const viewport = window.visualViewport
  if (viewport) {
    const { offsetTop: top, offsetLeft: left, width, height, scale } = viewport
    if (!dimension(scale)) return previous
    // A keyboard normally changes the visual rectangle at scale 1. At deliberate
    // zoom, remove inline geometry and let fixed inset-0 use the CSS layout viewport.
    // innerWidth/innerHeight may themselves shrink with zoom on mobile engines.
    // Focus alone does not establish that a scale change was automatic.
    if (Math.abs(scale - 1) >= 0.01) return null
    if ([top, left].every(finite) && [width, height].every(dimension)) return { top, left, width, height }
    // A transient zero/invalid visual rectangle must not collapse an active editor.
    if (previous) return previous
  }
  return null
}

/** Geometry only. Never moves focus, scrolls the document, or changes call ownership. */
export function useCallViewport(root: RefObject<HTMLElement>, onCommit?: () => void): CSSProperties | undefined {
  const [rect, setRect] = useState<ViewportRect | null>(() => readViewport(null))
  useLayoutEffect(() => {
    const viewport = window.visualViewport
    const element = root.current
    let frame: number | null = null
    const update = () => {
      frame = null
      setRect(previous => {
        const next = readViewport(previous)
        return previous && next && Object.keys(previous).every(key => previous[key as keyof ViewportRect] === next[key as keyof ViewportRect]) ? previous : next
      })
    }
    const schedule = () => { if (frame === null) frame = window.requestAnimationFrame(update) }
    update()
    viewport?.addEventListener("resize", schedule)
    viewport?.addEventListener("scroll", schedule)
    window.addEventListener("resize", schedule)
    window.addEventListener("orientationchange", schedule)
    element?.addEventListener("focusin", schedule)
    element?.addEventListener("focusout", schedule)
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame)
      viewport?.removeEventListener("resize", schedule)
      viewport?.removeEventListener("scroll", schedule)
      window.removeEventListener("resize", schedule)
      window.removeEventListener("orientationchange", schedule)
      element?.removeEventListener("focusin", schedule)
      element?.removeEventListener("focusout", schedule)
    }
  }, [root])
  // Run after DOM styles commit. A position-only move has no ResizeObserver
  // notification; native refresh synchronously invalidates earlier ACK revisions.
  useLayoutEffect(() => { onCommit?.() }, [rect, onCommit])
  return rect ? { ...rect, right: "auto", bottom: "auto" } : undefined
}
